import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { toast } from 'react-toastify'
import styled from 'styled-components/macro'

import {
  resetRecentChangesLastBrowsedSection,
  resetUserActivityLastBrowsedSection,
} from '../../redux/activitySlice'
import {
  duplicateIntoNewLocation,
  fetchLocationData,
  initNewLocation,
  setFromSettings,
  setIsBeingEditedAndResetPosition,
} from '../../redux/locationSlice'
import { setInitialView } from '../../redux/mapSlice'
import { viewFromCurrentUrl } from '../../utils/appUrl'
import { useAppHistory } from '../../utils/useAppHistory'
import { useIsDesktop, useIsEmbed } from '../../utils/useBreakpoint'
import useLocationPane from '../entry/useLocationPane'
import Button from '../ui/Button'

const LessPaddingButton = styled(Button)`
  padding: 0 10px;
`

const ToastContent = () => {
  const { t } = useTranslation()
  const history = useAppHistory()
  const dispatch = useDispatch()
  const view = viewFromCurrentUrl()
  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: '10px',
        width: '100%',
      }}
    >
      {t('success_message.location_submitted')}
      <div
        style={{
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '10px',
          width: '100%',
          flexWrap: 'wrap',
        }}
      >
        <LessPaddingButton
          type="button"
          onClick={() => {
            dispatch(duplicateIntoNewLocation())
            history.push(`/locations/init`)
          }}
        >
          {t('locations.add_similar')}
        </LessPaddingButton>
        <LessPaddingButton
          type="button"
          secondary
          onClick={() => {
            dispatch(initNewLocation(view.center))
            history.push(`/locations/init`)
          }}
        >
          {t('locations.add_new')}
        </LessPaddingButton>
      </div>
    </div>
  )
}

export const viewFromLocationCenter = ({ lat, lng }) => ({
  center: { lat, lng },
  zoom: 16,
})

const locationLoaded = (location, locationId) =>
  location && `${locationId}` === `${location.id}`

const ConnectLocation = ({
  locationId,
  isBeingEdited,
  isBeingEditedPosition,
  isSuccessfullyAdded,
}) => {
  const dispatch = useDispatch()
  const { initialView, googleMap } = useSelector((state) => state.map)
  const hasInitialView = !!initialView
  const { position, location } = useSelector((state) => state.location)
  const { drawerFullyOpen } = useLocationPane()
  const { isOpenInMobileLayout: filterOpen } = useSelector(
    (state) => state.filter,
  )
  const history = useAppHistory()
  const isDesktop = useIsDesktop()
  const isEmbed = useIsEmbed()
  const [hasCentered, setHasCentered] = useState(false)
  const { t } = useTranslation()
  const toastIdRef = useRef(null)

  useEffect(() => {
    if (locationLoaded(location, locationId)) {
      /*
       * We redo this effect each time locationId changes
       * but the component itself could be getting rerendered
       * (e.g. location to settings and back)
       * so if data is present in redux, don't fetch
       * */
      return
    }
    dispatch(
      fetchLocationData({
        locationId,
        isBeingEdited,
        isFromEmbedViewMap: isEmbed,
      }),
    ).then((action) => {
      if (fetchLocationData.rejected.match(action)) {
        history.push('/map')
        toast.error(
          t('error_message.api.fetch_location_failed', {
            id: locationId,
            message: action.error.message || t('error_message.unknown_error'),
          }),
        )
      }

      if (action.payload && !initialView) {
        dispatch(
          setInitialView(
            viewFromCurrentUrl() || viewFromLocationCenter(action.payload),
          ),
        )
      }
    })
  }, [dispatch, locationId]) //eslint-disable-line

  useEffect(() => {
    if (location && !hasInitialView) {
      dispatch(
        setInitialView(
          viewFromCurrentUrl() || viewFromLocationCenter(location),
        ),
      )
    }
  }, [!!location, hasInitialView]) //eslint-disable-line

  const hasCurrentUrl = !!viewFromCurrentUrl()
  const hasGoogleMap = !!googleMap
  const hasLocation = !!location

  useEffect(() => {
    if (
      hasInitialView &&
      !viewFromCurrentUrl() &&
      googleMap &&
      locationLoaded(location, locationId)
    ) {
      history.replaceView(viewFromLocationCenter(location))
    }
    //eslint-disable-next-line
  }, [
    dispatch,
    hasInitialView,
    hasCurrentUrl,
    hasGoogleMap,
    hasLocation,
    locationId,
  ])

  useEffect(() => {
    dispatch(setIsBeingEditedAndResetPosition(isBeingEdited))
  }, [dispatch, isBeingEdited])

  useEffect(
    () => () => {
      dispatch(setFromSettings(false))
      dispatch(resetRecentChangesLastBrowsedSection())
      dispatch(resetUserActivityLastBrowsedSection())
    },
    [dispatch, locationId],
  )

  useEffect(() => {
    if (
      isBeingEdited &&
      isBeingEditedPosition &&
      position &&
      !isDesktop &&
      googleMap &&
      !hasCentered
    ) {
      googleMap.setCenter(position)
      if (googleMap.getZoom() < 16) {
        googleMap.setZoom(16)
      }
      setHasCentered(true)
    }
  }, [
    isBeingEdited,
    isBeingEditedPosition,
    position,
    isDesktop,
    googleMap,
    hasCentered,
  ])

  useEffect(() => {
    setHasCentered(false)
  }, [locationId])

  const initialUIState = !drawerFullyOpen && !filterOpen && !isBeingEdited
  useEffect(() => {
    if (!initialUIState) {
      // Opening drawer or filter dismisses any toasts we created
      if (toastIdRef.current) {
        toast.dismiss(toastIdRef.current)
        toastIdRef.current = null
      }
    } else if (isSuccessfullyAdded) {
      toastIdRef.current = toast.success(
        <ToastContent locationId={locationId} />,
        {
          autoClose: false,
          style: { width: '100%' },
        },
      )
      /*
       * We don't want to toast again after user presses back in the browser
       * so remove 'success' from URL
       * the effect will re-run, with isSuccessfullyAdded false this time
       */
      history.replace(`/locations/${locationId}`)
    } else {
      /*
       * Closed drawer, but still on location page
       * We're probably here after history.replace happened
       * The user might have closed the toast or be still looking at it
       * Prepare to close our toast after navigating away if it is still there
       */
      return () => {
        if (
          window.location.href.indexOf(`/locations/${locationId}`) === -1 &&
          toastIdRef.current
        ) {
          toast.dismiss(toastIdRef.current)
          toastIdRef.current = null
        }
      }
    }
  }, [isSuccessfullyAdded, initialUIState, locationId]) //eslint-disable-line

  return null
}

export default ConnectLocation
